import { appKey } from '@app/shared/brand'

/**
 * What somebody answered on the membership branch, kept across the card page.
 *
 * Its own store beside the gift's rather than more fields on that one: the two
 * branches share no question but the shul, and one shape carrying both would be
 * a guard that rejects every stored answer the day either branch grows a field.
 *
 * `sessionStorage`, one key per org, so two shuls open in two tabs cannot read
 * each other's.
 */
export type MembershipAnswers = {
  tierId: string
  tierLabel: string
  cadence: 'monthly' | 'annual'
  startsOn: string
  /** What is charged today. Zero where the membership begins later. */
  dueTodayCents: number
  /** What each period costs, which is what the first payment will be. */
  rateCents: number
  joining: boolean
  donorName: string
  donorEmail: string
  /** The checkout these answers were opened as, once one has opened. */
  checkoutId?: string
}

const keyFor = (orgId: string) => appKey('membership', orgId)

export function rememberMembership(
  orgId: string,
  answers: MembershipAnswers,
): void {
  try {
    sessionStorage.setItem(keyFor(orgId), JSON.stringify(answers))
  } catch {
    /* A browser refusing storage still takes a membership; only the resume
       after a failed payment is lost. */
  }
}

export function recallMembership(orgId: string): MembershipAnswers | null {
  try {
    const raw = sessionStorage.getItem(keyFor(orgId))
    if (!raw) return null
    const value: unknown = JSON.parse(raw)
    return isAnswers(value) ? value : null
  } catch {
    return null
  }
}

export function forgetMembership(orgId: string): void {
  try {
    sessionStorage.removeItem(keyFor(orgId))
  } catch {
    /* Nothing to clear if nothing could be stored. */
  }
}

/** Field by field, so a shape from an older page is rejected whole. */
function isAnswers(value: unknown): value is MembershipAnswers {
  if (typeof value !== 'object' || value === null) return false
  const v = value as Record<string, unknown>
  return (
    typeof v.tierId === 'string' &&
    typeof v.tierLabel === 'string' &&
    (v.cadence === 'monthly' || v.cadence === 'annual') &&
    typeof v.startsOn === 'string' &&
    typeof v.dueTodayCents === 'number' &&
    typeof v.rateCents === 'number' &&
    typeof v.joining === 'boolean' &&
    typeof v.donorName === 'string' &&
    typeof v.donorEmail === 'string' &&
    (v.checkoutId === undefined || typeof v.checkoutId === 'string')
  )
}
